import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, Chip, CardMedia, Box, Fade } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface PublishedBandCardProps {
  band: {
    _id: string;
    artistName: string;
    genres: string[];
    shortDescription: string;
    mainImage: string;
  };
  strandSlug: string;
  index?: number;
}

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};


const PublishedBandCard: React.FC<PublishedBandCardProps> = ({ band, strandSlug, index = 0 }) => {
  return (
    <Fade in timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
      <Link 
        to={`/strands/${strandSlug}/band/${band._id}`} 
        style={{ textDecoration: 'none', display: 'block', height: '100%' }}
      >
        <Card 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            cursor: 'pointer',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-8px) scale(1.02)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
              '& .card-media': {
                transform: 'scale(1.1)',
              },
              '& .card-overlay': {
                opacity: 1,
              },
            }
          }}
        >
          {/* Image with zoom effect */}
          <Box sx={{ position: 'relative', overflow: 'hidden', height: 200 }}>
            <CardMedia
              component="img"
              className="card-media"
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              image={band.mainImage || '/placeholder-image.jpg'}
              alt={band.artistName}
            />
            {/* Hover overlay */}
            <Box
              className="card-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'scale(0.9)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1)',
                  }
                }}
              >
                <PlayArrowIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
            </Box>
          </Box>

          <CardContent sx={{ flexGrow: 1 }}>
            {/* Band Name */}
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              color="text.primary"
              sx={{
                transition: 'all 0.3s ease',
                transformOrigin: 'left center',
                '.MuiCard-root:hover &': {
                  transform: 'scale(1.05)',
                  color: '#4fc3f7',
                }
              }}
            >
              {band.artistName}
            </Typography>

            {/* Short Description */}
           {band.shortDescription && (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
    {stripHtml(band.shortDescription).substring(0, 100)}
    {stripHtml(band.shortDescription).length > 100 && '...'}
  </Typography>
)}
            
            {/* Staggered chip animation */}
            <Box mt={1}>
              {band.genres.map((genre, idx) => (
                <Chip 
                  key={genre} 
                  label={genre} 
                  size="small" 
                  sx={{ 
                    margin: '0 2px 2px 0',
                    animation: `slideIn 0.3s ease forwards`,
                    animationDelay: `${idx * 0.05}s`,
                    opacity: 0,
                    '@keyframes slideIn': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(10px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }
                  }} 
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Link>
    </Fade>
  );
};

export default PublishedBandCard;